// src/components/Terminal.js

import React, { useEffect, useRef, forwardRef, memo } from 'react';
import BlinkingCursor from './BlinkingCursor';

/**
 * Terminal
 * --------
 * Renders typed "content" from SystemInterface.
 * Optimized for performance with memoization.
 */
const Terminal = memo(forwardRef(({
  content,
  showCursor,
  isTyping,
  cursorPosition,
  cursorBlinking
}, ref) => {
  const displayRef = useRef(null);

  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(displayRef.current);
      } else {
        ref.current = displayRef.current;
      }
    }
  }, [ref]);

  useEffect(() => {
    if (displayRef.current) {
      displayRef.current.scrollTop = displayRef.current.scrollHeight;
    }
  }, [content]);

  const formatResponse = (item, index) => {
    if (!item) return null;
    
    if (React.isValidElement(item)) return item;
    
    if (typeof item === 'string') return <span>{item}</span>;

    if (typeof item === 'object') {
      const { text, dots = '', type, className = '', useHtml = false } = item;

      if (React.isValidElement(text)) return text;
      
      if (typeof text !== 'string') return <span className={className}>{text}</span>;

      const cursorElement = showCursor && index === content.length - 1 && (
        cursorBlinking ? <BlinkingCursor /> : <span className="solid-cursor" />
      );
      
      const dotsElement = dots && <span className="dots">{dots}</span>;

      if (useHtml) {
        return (
          <>
            <span
              className={type === 'button' ? 'continue-button-container' : 'stream-text'}
              dangerouslySetInnerHTML={{ __html: text }}
            />
            {cursorElement}
            {dotsElement}
          </>
        );
      }

      if (type === 'bracket') {
        return <span className={`brackets ${className}`}>{text}</span>;
      }
      
      if (type === 'stream') {
        return (
          <>
            <span className={`stream-text ${className}`}>{text}</span>
            {cursorElement}
            {dotsElement}
          </>
        );
      }
      
      if (type === 'image') {
        return React.isValidElement(text) ? 
          <div className={`logo-container ${className}`}>{text}</div> :
          <div className={`logo-container ${className}`}>[Image data missing or invalid]</div>;
      }
      
      if (type === 'button') {
        return <span className={`continue-button-container ${className}`}>{text}</span>;
      }

      return (
        <span className={className}>
          {text}
          {cursorElement}
          {dotsElement}
        </span>
      );
    }

    return <span>{String(item)}</span>;
  };

  return (
    <div className="response-display" ref={displayRef}>
      {Array.isArray(content) && content.length > 0 ? (
        content.map((item, idx) => (
          <div 
            key={idx} 
            className={`content-item${item?.containerClass ? ` ${item.containerClass}` : ''}${item?.type === 'button' ? ' button-container' : ''}`}
          >
            {formatResponse(item, idx)}
          </div>
        ))
      ) : (
        <div>No content to display</div>
      )}
    </div>
  );
}));

export default Terminal;